import { lazy, Suspense } from "react";
import AuthGuard from "./components/AuthGuard";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import GuestGuard from "./components/GuestGuard";
import LoadingScreen from "./components/LoadingScreen";
import IndicesTotalOffers from "./pages/dashboard/IndicesTotalOffers";
import IndicesTotalTrips from "./pages/dashboard/IndicesTotalTrips";
import { privacy } from "./pages/dashboard/PrivacyPolicy";
import { Terms } from "./pages/dashboard/TermsOfService";
import DestinationByCountry from "./pages/prediction/DestinationByCountry";
import DestinationByDepartureDates from "./pages/prediction/DestinationByDepartureDates";
import DestinationByPrices from "./pages/prediction/DestinationByPrices";
import DestinationComparison from "./pages/prediction/DestinationComparison";
import OfferByCountry from "./pages/prediction/OfferByCountry";
import OfferByDepartureDates from "./pages/prediction/OfferByDepartureDates";
import OfferComparison from "./pages/prediction/OfferComparison";
import PredictionOverview from "./pages/prediction/Overview";

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

// Authentication pages

const Login = Loadable(lazy(() => import("./pages/authentication/Login")));

// Dashboard pages

const Account = Loadable(lazy(() => import("./pages/dashboard/Account")));
const Analytics = Loadable(lazy(() => import("./pages/dashboard/Analytics")));
const AnalyticsList = Loadable(
  lazy(() => import("./pages/dashboard/AnalyticsList"))
);
const AnalyticsTopSearchesList = Loadable(
  lazy(() => import("./pages/dashboard/AnalyticsTopSearchesList"))
);
const AnalyticsTopResultsList = Loadable(
  lazy(() => import("./pages/dashboard/AnalyticsTopResultsList"))
);
const AnalyticsSearchesWithoutResultsList = Loadable(
  lazy(() => import("./pages/dashboard/AnalyticsSearchesWithoutResultsList"))
);
const AnalyticsTopFiltersList = Loadable(
  lazy(() => import("./pages/dashboard/AnalyticsTopFiltersList"))
);
const AnalyticsTopAttributesList = Loadable(
  lazy(() => import("./pages/dashboard/AnalyticsTopAttributesList"))
);
const AnalyticsPopularValuesForFilterList = Loadable(
  lazy(() => import("./pages/dashboard/AnalyticsPopularValuesForFilterList"))
);
const KeywordTrends = Loadable(
  lazy(() => import("./pages/dashboard/KeyWordsTrends"))
);
const SearchTrend = Loadable(
  lazy(() => import("./pages/dashboard/SearchTrends"))
);
const AnalyticsTotalOffers = Loadable(
  lazy(() => import("./pages/dashboard/AnalyticsTotalOffers"))
);
const AnalyticsTotalDocuments = Loadable(
  lazy(() => import("./pages/dashboard/AnalyticsTotalDocuments"))
);
const ChangeLog = Loadable(lazy(() => import("./pages/dashboard/ChangeLogs")));
const PrivacyOrTerms = Loadable(
  lazy(() => import("./pages/dashboard/PrivacyOrTerms"))
);

const Main = Loadable(lazy(() => import("./pages/dashboard/Main")));
const Overview = Loadable(lazy(() => import("./pages/dashboard/Overview")));
const Indices = Loadable(lazy(() => import("./pages/dashboard/Indices")));

const IndicesOffersDetails = Loadable(
  lazy(() => import("./pages/dashboard/IndicesOffersDetails"))
);

const IndicesTrips = Loadable(
  lazy(() => import("./pages/dashboard/IndicesTrips"))
);

const IndicesTripsDetails = Loadable(
  lazy(() => import("./pages/dashboard/IndicesTripsDetails"))
);

const Monitoring = Loadable(lazy(() => import("./pages/dashboard/Monitoring")));

const Importing = Loadable(lazy(() => import("./pages/dashboard/Importing")));

const RulesNew = Loadable(lazy(() => import("./pages/dashboard/RulesNew")));

const RulesSearch = Loadable(
  lazy(() => import("./pages/dashboard/RulesSearch"))
);
const MerchandisingOverview = Loadable(
  lazy(() => import("./pages/dashboard/MerchandisingOverview"))
);
const RulesOffer = Loadable(lazy(() => import("./pages/dashboard/RulesOffer")));

const AuthorizationRequired = Loadable(
  lazy(() => import("./pages/AuthorizationRequired"))
);
const NotFound = Loadable(lazy(() => import("./pages/NotFound")));
const ServerError = Loadable(lazy(() => import("./pages/ServerError")));

// Other pages

const routes = [
  {
    path: "authentication",
    children: [
      {
        path: "login",
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      {
        path: "login-unguarded",
        element: <Login />,
      },
    ],
  },
  {
    path: "dashboard",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "main",
        element: <Main />,
      },
      {
        path: "overview",
        element: <Overview />,
      },
      {
        path: "changelog",
        element: <ChangeLog />,
        children: [
          {
            path: ":logId",
            element: <ChangeLog />,
          },
        ],
      },
      {
        path: "privacy-policy",
        element: (
          <PrivacyOrTerms contentText={privacy} termsOrPrivacy={"Privacy"} />
        ),
      },
      {
        path: "terms-of-service",
        element: (
          <PrivacyOrTerms contentText={Terms} termsOrPrivacy={"Terms"} />
        ),
      },
      {
        path: "account",
        element: <Account />,
      },
      {
        path: "data-exploration",
        element: <Analytics />,
      },
      {
        path: "analytics-list",
        element: <AnalyticsList />,
      },
      {
        path: "analytics-top-searches",
        element: <AnalyticsTopSearchesList />,
      },
      {
        path: "analytics-top-results",
        element: <AnalyticsTopResultsList />,
      },
      {
        path: "analytics-searches-without-results",
        element: <AnalyticsSearchesWithoutResultsList />,
      },
      {
        path: "analytics-top-filters",
        element: <AnalyticsTopFiltersList />,
      },
      {
        path: "analytics-top-attributes",
        element: <AnalyticsTopAttributesList />,
      },
      {
        path: "analytics-popular-values-for-filter",
        element: <AnalyticsPopularValuesForFilterList />,
      },
      {
        path: "analytics-total-offers",
        element: <AnalyticsTotalOffers />,
      },
      {
        path: "analytics-total-documents",
        element: <AnalyticsTotalDocuments />,
      },
      /*
      {
        path: "invoices",
        children: [
          {
            path: "/",
            element: <InvoiceList />,
          },
          {
            path: ":invoiceId",
            element: <InvoiceDetails />,
          },
        ],
      },
       */
      {
        path: "indices",
        children: [
          {
            path: "",
            element: <Indices />,
          },
          {
            path: ":id/terms/:index",
            element: <IndicesTripsDetails />,
          },
          {
            path: ":id/terms",
            element: <IndicesTrips />,
          },
          {
            path: ":id",
            element: <IndicesOffersDetails />,
          },
        ],
      },
      {
        path: "indices-total-offers",
        element: <IndicesTotalOffers />,
      },
      {
        path: "indices-total-trips",
        element: <IndicesTotalTrips />,
      },
      {
        path: "monitoring",
        element: <Monitoring />,
      },
      {
        path: "importing",
        element: <Importing />,
      },
      {
        path: "rules",
        children: [
          {
            path: "search",
            children: [
              { path: "", element: <RulesSearch /> },
              {
                path: "new",
                children: [
                  { path: "", element: <RulesNew /> },
                  { path: ":id", element: <RulesNew /> },
                ],
              },
            ],
          },
          {
            path: "offers",
            children: [
              { path: "", element: <RulesOffer /> },
              {
                path: "new",
                children: [
                  { path: "", element: <RulesNew /> },
                  { path: ":id", element: <RulesNew /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "merchandising",
        children: [
          {
            path: "",
            element: <MerchandisingOverview />,
          },
          {
            path: "search",
            children: [
              { path: "", element: <RulesSearch /> },
              {
                path: "new",
                children: [
                  { path: "", element: <RulesNew /> },
                  { path: ":id", element: <RulesNew /> },
                ],
              },
            ],
          },
          {
            path: "offers",
            children: [
              { path: "", element: <RulesOffer /> },
              {
                path: "new",
                children: [
                  { path: "", element: <RulesNew /> },
                  { path: ":id", element: <RulesNew /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "/dashboard/analytics",
        children: [
          {
            path: "",
            element: <PredictionOverview />,
          },
          {
            path: "destination/country",
            element: <DestinationByCountry />,
          },
          {
            path: "destination/prices",
            element: <DestinationByPrices />,
          },
          {
            path: "destination/departure-dates",
            element: <DestinationByDepartureDates />,
          },
          {
            path: "destination/comparison",
            element: <DestinationComparison />,
          },
          {
            path: "offer/country",
            element: <OfferByCountry />,
          },
          {
            path: "offer/departure-dates",
            element: <OfferByDepartureDates />,
          },
          {
            path: "offer/comparison",
            element: <OfferComparison />,
          },
          {
            path: "keywords",
            element: <KeywordTrends />,
          },
          {
            path: "search",
            element: <SearchTrend />,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Login />,
    children: [
      {
        path: "401",
        element: <AuthorizationRequired />,
      },
      {
        path: "404",
        element: <NotFound />,
      },
      {
        path: "500",
        element: <ServerError />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];

export default routes;
